import React, { useEffect, useState, useRef } from 'react';
import '../styles/AgmDocumentsDash.css';
import '../styles/WhitePapersDash.css';
import { FaPlusCircle, FaFileAlt, FaFilePdf, FaTrash } from 'react-icons/fa';
import { RiCheckFill, RiCloseFill, RiFileShield2Line, RiToggleFill, RiToggleLine } from 'react-icons/ri';
import { useSlideout } from '../../../hooks/SlideoutContext';
import { IoMdRefresh } from 'react-icons/io';
import axios from 'axios';
import config from '../../../constants/config';

const AUTH = "b8GjLbLHDX9isF10ptWj";

const COMMITTEES = [
  'Regulatory Committee',
  'Technical Committee',
  'Commercial Committee',
  'Policy Committee',
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 15 }, (_, i) => CURRENT_YEAR - i);

export default function WhitePapersDash() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterYear, setFilterYear] = useState('all');
  const [filterCommittee, setFilterCommittee] = useState('all');
  const { openSlideout } = useSlideout();

  const fetchPapers = () => {
    setLoading(true);
    axios.get(config.api.url + 'admin/white_papers/fetch', {
      headers: { api: config.api.key, auth: AUTH }
    })
      .then(({ data }) => {
        if (data.success) setPapers(data.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const toggleStatus = (id, current) => {
    const newStatus = current > 0 ? 0 : 1;
    const fd = new FormData();
    fd.append('id', id);
    fd.append('status', newStatus);
    axios.post(config.api.url + 'admin/white_papers/toggle', fd, {
      headers: { api: config.api.key, auth: AUTH, 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => { if (data.success) fetchPapers(); });
  };

  const deletePaper = (id) => {
    if (!window.confirm('Are you sure you want to delete this white paper?')) return;
    const fd = new FormData();
    fd.append('id', id);
    axios.post(config.api.url + 'admin/white_papers/delete', fd, {
      headers: { api: config.api.key, auth: AUTH, 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => { if (data.success) fetchPapers(); });
  };

  useEffect(() => { fetchPapers(); }, []);

  const getFileIcon = (fileType) => {
    if (!fileType) return <FaFileAlt />;
    if (fileType.includes('pdf')) return <FaFilePdf className="file-icon pdf" />;
    return <FaFileAlt className="file-icon" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const allCommittees = [...new Set(papers.map(p => p.committee))].filter(Boolean);

  const filtered = papers.filter(p => {
    const yearOk = filterYear === 'all' || String(p.year) === String(filterYear);
    const comOk  = filterCommittee === 'all' || p.committee === filterCommittee;
    return yearOk && comOk;
  });

  // ──────────────────── Inline Edit/Add form ────────────────────
  const EditForm = ({ paper, fetchPapers }) => {
    const { closeSlideout } = useSlideout();
    const fileInputRef = useRef(null);

    const [form, setForm] = useState({
      id:          paper.id || null,
      title:       paper.title || '',
      description: paper.description || '',
      committee:   paper.committee || 'Regulatory Committee',
      year:        paper.year || CURRENT_YEAR,
      sort_order:  paper.sort_order || 0,
      active:      paper.active !== undefined ? paper.active : 1,
    });

    const [selectedFile, setSelectedFile]       = useState(null);
    const [selectedFileName, setSelectedFileName] = useState(paper.file_name || '');

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedFile(file);
        setSelectedFileName(file.name);
      }
    };

    const handleSave = () => {
      if (!form.title) { alert('Please enter a title.'); return; }
      if (!form.year)  { alert('Please enter a year.');  return; }
      if (!form.id && !selectedFile) { alert('Please select a document file.'); return; }

      const fd = new FormData();
      if (form.id) fd.append('id', form.id);
      fd.append('title',       form.title);
      fd.append('description', form.description);
      fd.append('committee',   form.committee);
      fd.append('year',        form.year);
      fd.append('sort_order',  form.sort_order);
      fd.append('active',      form.active);
      if (selectedFile) fd.append('document', selectedFile);

      const endpoint = form.id ? 'admin/white_papers/edit' : 'admin/white_papers/add';
      axios.post(config.api.url + endpoint, fd, {
        headers: { api: config.api.key, auth: AUTH, 'Content-Type': 'multipart/form-data' }
      }).then(({ data }) => {
        if (data.success) { closeSlideout(); fetchPapers(); }
        else alert('Error: ' + data.response);
      }).catch(() => alert('An error occurred while saving.'));
    };

    return (
      <div className="AgmDocumentEditForm">
        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Title *</div>
          <input
            type="text" name="title" value={form.title}
            onChange={handleChange} className="AgmDocumentEditFormInput"
            placeholder="Enter paper title"
          />
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Description</div>
          <textarea
            name="description" value={form.description}
            onChange={handleChange} className="AgmDocumentEditFormTextarea"
            placeholder="Brief description (optional)" rows={3}
          />
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Committee *</div>
          <select name="committee" value={form.committee} onChange={handleChange} className="AgmDocumentEditFormSelect">
            {COMMITTEES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Publication Year *</div>
          <select name="year" value={form.year} onChange={handleChange} className="AgmDocumentEditFormSelect">
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Sort Order</div>
          <input
            type="number" name="sort_order" value={form.sort_order}
            onChange={handleChange} className="AgmDocumentEditFormInput"
            placeholder="0" min="0"
          />
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Document File {!form.id && '*'}</div>
          <div className="AgmDocumentEditFormFilePreview">
            {selectedFileName && (
              <div className="AgmDocumentEditFormFileName"><FaFileAlt /> {selectedFileName}</div>
            )}
            <button onClick={() => fileInputRef.current.click()} className="AgmDocumentEditFormFileButton">
              {form.id ? 'Replace File' : 'Select File'}
            </button>
            <input
              type="file" ref={fileInputRef} onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              style={{ display: 'none' }}
            />
            <div className="AgmDocumentEditFormFileHint">Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT</div>
          </div>
        </div>

        <div className="AgmDocumentEditFormRow">
          <label className="AgmDocumentEditFormCheckbox">
            <input type="checkbox" name="active" checked={form.active === 1} onChange={handleChange} />
            <span>Active (visible on website)</span>
          </label>
        </div>

        <div className="AgmDocumentEditFormActions">
          <button onClick={closeSlideout} className="AgmDocumentEditFormButton Cancel">Cancel</button>
          <button onClick={handleSave} className="AgmDocumentEditFormButton Save">
            {form.id ? 'Save Changes' : 'Add White Paper'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="AgmDocumentsDashWrapper">
      <div className="AgmDocumentsDashHeader">
        <div className="AgmDocumentsDashHeaderTitle">
          <h2>White Papers</h2>
          <p>Manage CANTO white papers by committee and year</p>
        </div>
        <div className="AgmDocumentsDashHeaderActions">
          <button className="AgmDocumentsDashHeaderButton Refresh" onClick={fetchPapers}>
            <IoMdRefresh /><span>Refresh</span>
          </button>
          <button
            className="AgmDocumentsDashHeaderButton Add"
            onClick={() => openSlideout({
              title: 'Add New White Paper',
              content: <EditForm paper={{}} fetchPapers={fetchPapers} />
            })}
          >
            <FaPlusCircle /><span>Add New</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="WPDashFilters">
        <select value={filterYear} onChange={e => setFilterYear(e.target.value)} className="WPDashFilterSelect">
          <option value="all">All Years</option>
          {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={filterCommittee} onChange={e => setFilterCommittee(e.target.value)} className="WPDashFilterSelect">
          <option value="all">All Committees</option>
          {allCommittees.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="WPDashCount">{filtered.length} paper{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="AgmDocumentsDashContent">
        {loading ? (
          <div className="AgmDocumentsDashLoading">Loading white papers...</div>
        ) : (
          <>
            <div className="AgmDocumentsContentNavigation">
              <div className="AgmDocumentsContentNavigationItem First">Status</div>
              <div className="AgmDocumentsContentNavigationItem Type">Type</div>
              <div className="AgmDocumentsContentNavigationItem Title">Title</div>
              <div className="AgmDocumentsContentNavigationItem WPCommittee">Committee</div>
              <div className="AgmDocumentsContentNavigationItem WPYear">Year</div>
              <div className="AgmDocumentsContentNavigationItem Size">Size</div>
              <div className="AgmDocumentsContentNavigationItem Date">Added</div>
              <div className="AgmDocumentsContentNavigationItem Actions">Actions</div>
            </div>
            <div className="AgmDocumentsScroll">
              {filtered.length === 0 ? (
                <div className="AgmDocumentsEmpty">
                  <FaFileAlt className="EmptyIcon" />
                  <p>No white papers found. Click "Add New" to upload your first paper.</p>
                </div>
              ) : (
                filtered.map(item => (
                  <div className="AgmDocumentItem" key={item.id}>
                    <div className="AgmDocumentItemDetailsText First Active">
                      <span className={`AgmDocumentItemDetailsTextOption Circle ${item.active > 0 ? 'Success' : 'Error'}`}>
                        {item.active > 0
                          ? <RiCheckFill style={{ fontSize: 12 }} />
                          : <RiCloseFill style={{ fontSize: 12 }} />
                        }
                      </span>
                    </div>
                    <div className="AgmDocumentItemDetailsText Type">
                      {getFileIcon(item.file_type)}
                    </div>
                    <div className="AgmDocumentItemDetailsText Title Bold">{item.title}</div>
                    <div className="AgmDocumentItemDetailsText WPCommittee">{item.committee}</div>
                    <div className="AgmDocumentItemDetailsText WPYear">
                      <span className="WPYearBadge">{item.year}</span>
                    </div>
                    <div className="AgmDocumentItemDetailsText Size">{formatFileSize(item.file_size)}</div>
                    <div className="AgmDocumentItemDetailsText Date">{formatDate(item.timestamp)}</div>

                    <div className="AgmDocumentItemOptions">
                      <a
                        onClick={() => toggleStatus(item.id, item.active)}
                        className={`AgmDocumentItemOptionsButton ${item.active > 0 ? 'DeleteInverted' : 'AddInverted'}`}
                      >
                        {item.active > 0
                          ? <RiToggleFill style={{ fontSize: 14 }} />
                          : <RiToggleLine style={{ fontSize: 14 }} />
                        }
                        <span className="AgmDocumentItemOptionsButtonText">
                          {item.active > 0 ? 'Deactivate' : 'Activate'}
                        </span>
                      </a>

                      <a
                        onClick={() => openSlideout({
                          title: 'Edit White Paper',
                          content: <EditForm paper={item} fetchPapers={fetchPapers} />
                        })}
                        className="AgmDocumentItemOptionsButton Edit"
                      >
                        <RiFileShield2Line style={{ fontSize: 14 }} />
                        <span className="AgmDocumentItemOptionsButtonText">Edit</span>
                      </a>

                      <a
                        onClick={() => deletePaper(item.id)}
                        className="AgmDocumentItemOptionsButton Delete"
                      >
                        <FaTrash style={{ fontSize: 12 }} />
                        <span className="AgmDocumentItemOptionsButtonText">Delete</span>
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
